import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  type ConnectProposalDto,
  confirmConnectProposal,
  dismissConnectProposal,
  getConnectConfig,
  getConnectProposals,
  sendConnectInvite,
  serverErrorMessage,
} from '../../lib/api.js';
import { formatRelative } from '../../lib/format.js';
import { LIVE_POLL_MS } from '../../lib/poll.js';
import { QueuePage, type QueueTab } from './QueuePage.js';

/**
 * batch-301 (connect-with-meeting-people, Phase 1b) — the "People you met" hub. A gated,
 * throttled scan proposes LinkedIn profiles for EXTERNAL meeting attendees; here the operator
 * picks the right person (Confirm) or Dismisses. READ-ONLY — no sending in Phase 1 (the gated
 * invite is a future Phase 2; a confirmed proposal just locks the chosen profile).
 */
export function ConnectView() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<QueueTab>('queue');

  const feed = useQuery({
    queryKey: ['connect-proposals'],
    queryFn: ({ signal }) => getConnectProposals({ signal }),
    refetchInterval: LIVE_POLL_MS,
  });
  const cfg = useQuery({
    queryKey: ['connect-config'],
    queryFn: ({ signal }) => getConnectConfig({ signal }),
    staleTime: 60_000,
  });
  const sendArmed = Boolean(cfg.data?.send_enabled);
  const items = feed.data?.items ?? [];

  useEffect(() => {
    if (items.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (selectedId === null || !items.some((i) => i.id === selectedId)) {
      setSelectedId(items[0]?.id ?? null);
    }
  }, [items, selectedId]);

  return (
    <QueuePage<ConnectProposalDto>
      testId="connect-view"
      title="People you met"
      count={items.length}
      items={items}
      getId={(i) => i.id}
      selectedId={selectedId}
      onSelect={setSelectedId}
      rowEstimate={64}
      isLoading={feed.isLoading}
      isError={feed.isError}
      onRetry={() => void feed.refetch()}
      emptyTitle="No one to connect with"
      emptyHint="External people you met appear here after the LinkedIn match scan runs."
      tab={tab}
      onTabChange={setTab}
      capabilityBadge={
        <span data-testid="connect-capability">
          {sendArmed ? 'confirm · connect' : 'review · confirm the match'}
        </span>
      }
      renderRow={(item) => <ProposalRowSummary item={item} />}
      renderCanvas={(item) =>
        item ? (
          <ProposalCanvas
            key={item.id}
            item={item}
            sendArmed={sendArmed}
            template={cfg.data?.invite_template ?? ''}
          />
        ) : (
          <p className="queue-canvas-empty">Select a person to review their LinkedIn matches.</p>
        )
      }
    />
  );
}

// Map LinkedIn's network_distance ("DISTANCE_2") → a short badge, WITHOUT a 'DISTANCE_n'
// string literal (the web-copy-lint guard flags UPPER_SNAKE literals as env-var leaks).
function distanceBadge(nd: string | null): string | null {
  const n = (nd ?? '').split('_')[1];
  return n === '2' ? '2nd' : n === '3' ? '3rd' : null;
}

function ProposalRowSummary({ item }: { item: ConnectProposalDto }) {
  return (
    <span className="jc-row">
      <span className="jc-row-headline">{item.attendee_name ?? 'A contact'}</span>
      <span className="jc-row-meta">
        {item.attendee_company ? <span className="jc-kind">{item.attendee_company}</span> : null}
        <span className="jc-dot jc-dot-pending">{item.candidates.length} match(es)</span>
        {item.meeting_start_at ? <time>{formatRelative(item.meeting_start_at)}</time> : null}
      </span>
    </span>
  );
}

function ProposalCanvas({
  item,
  sendArmed,
  template,
}: {
  item: ConnectProposalDto;
  sendArmed: boolean;
  template: string;
}) {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const [sentLocal, setSentLocal] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const confirmMut = useMutation({
    mutationFn: (providerId: string) => confirmConnectProposal(item.id, providerId),
    onSuccess: () => {
      setErr(null);
      void qc.invalidateQueries({ queryKey: ['connect-proposals'] });
    },
    onError: (e) => setErr(serverErrorMessage(e, 'confirm failed')),
  });
  const dismissMut = useMutation({
    mutationFn: () => dismissConnectProposal(item.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['connect-proposals'] }),
    onError: (e) => setErr(serverErrorMessage(e, 'dismiss failed')),
  });
  const sendMut = useMutation({
    mutationFn: () => sendConnectInvite(item.id, (note ?? template).trim() || undefined),
    onSuccess: () => {
      setSentLocal(true);
      void qc.invalidateQueries({ queryKey: ['connect-proposals'] });
    },
    onError: (e) => setErr(serverErrorMessage(e, 'send failed')),
  });

  const sent = item.status === 'sent' || sentLocal;
  const confirmed = item.status === 'confirmed' && !sent;
  const chosen = confirmed
    ? item.candidates.find((c) => c.providerId === item.chosen_provider_id)
    : null;

  return (
    <div className="jc-canvas" data-testid="connect-canvas">
      <div className="jc-canvas-head">
        <h2>{item.attendee_name ?? 'A contact'}</h2>
        {!sent ? (
          <button
            type="button"
            className="jc-dismiss"
            onClick={() => dismissMut.mutate()}
            disabled={dismissMut.isPending}
          >
            Dismiss
          </button>
        ) : null}
      </div>
      <p className="jc-hint">
        Met{item.meeting_subject ? ` at “${item.meeting_subject}”` : ''}
        {item.attendee_email ? ` · ${item.attendee_email}` : ''}
        {item.attendee_company ? ` · ${item.attendee_company}` : ''}.{' '}
        {sendArmed
          ? 'Confirm the right profile, then send a connection request.'
          : 'Pick the right LinkedIn profile (sending is off).'}
      </p>

      {sent ? (
        <p className="jc-sent" data-testid="connect-sent">
          Connection request sent ✓
        </p>
      ) : confirmed ? (
        <div className="connect-confirmed">
          <p className="connect-cand-main" data-testid="connect-confirmed">
            <strong>{chosen?.name ?? 'Selected profile'}</strong>
            {chosen?.headline ? (
              <span className="connect-cand-headline">{chosen.headline}</span>
            ) : null}
          </p>
          {sendArmed ? (
            <>
              <textarea
                value={note ?? template}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full rounded border border-zinc-700 bg-zinc-950 px-2 py-1 text-[12px] text-zinc-100 outline-none focus:border-zinc-500"
                aria-label="Connection request note"
              />
              <button
                type="button"
                className="jc-btn jc-send"
                data-testid="connect-send"
                disabled={sendMut.isPending}
                onClick={() => sendMut.mutate()}
              >
                {sendMut.isPending ? 'Sending…' : 'Send connection request'}
              </button>
            </>
          ) : (
            <span className="unified-side-note">
              Ready to connect — sending is off in your configuration.
            </span>
          )}
        </div>
      ) : item.candidates.length === 0 ? (
        <p className="jc-muted">No LinkedIn matches were found for this person.</p>
      ) : (
        <ul className="connect-candidates">
          {item.candidates.map((c) => (
            // Candidates from the scan always carry a providerId (the filter requires it);
            // the joined fallback only guards a hand-built/degenerate row.
            <li key={c.providerId || `${c.profileUrl ?? ''}|${c.name ?? ''}`}>
              <span className="connect-cand-main">
                <strong>{c.name ?? 'Unknown'}</strong>
                {distanceBadge(c.networkDistance) ? (
                  <span className="jc-kind">{distanceBadge(c.networkDistance)}</span>
                ) : null}
                {c.headline ? <span className="connect-cand-headline">{c.headline}</span> : null}
                {c.location ? <span className="unified-side-note">{c.location}</span> : null}
              </span>
              <span className="connect-cand-actions">
                {c.profileUrl ? (
                  <a
                    href={c.profileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="today-meeting-contact"
                  >
                    View profile ↗
                  </a>
                ) : null}
                <button
                  type="button"
                  className="jc-btn jc-send"
                  disabled={!c.providerId || confirmMut.isPending}
                  onClick={() => c.providerId && confirmMut.mutate(c.providerId)}
                >
                  This is them ✓
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {err ? <p className="jc-failed">{err}</p> : null}
    </div>
  );
}
